import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { getCustomers } from "src/api/customer";
import { createSubAccount, editSubAccount } from "src/api/subAccount";
import ComboSearch from "src/components/ComboSearch";
import { Field, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { getCustomizationValue, showSuccessToast } from "src/lib/utils";
import type { CustomerRow, SubAccount } from "src/types/customer";

const validationSchema = Yup.object().shape({
	firstname: Yup.string().required("This field is required"),
	lastname: Yup.string().required("This field is required"),
	othername: Yup.string(),
	idNo: Yup.string(),
	customer_id: Yup.string().required("This field is required"),
	businessUuid: Yup.string().required("This field is required"),
});

interface SubAccountFormValues {
	uuid?: string;
	firstname: string;
	lastname: string;
	othername: string;
	idNo: string;
	customer_id: string;
	businessUuid: string;
}

export interface SubAccountFormProps {
	/** Parent customer. Omit and pass `pickParent` to let the user search for one. */
	customer_id?: string | number;
	/** Only supplied when editing an existing record. */
	subAccount?: SubAccount;
	/** Renders the customer picker instead of taking the parent from `customer_id`. */
	pickParent?: boolean;
	/** Query key to invalidate on success — the two entry points list sub accounts differently. */
	invalidateKey: string;
}

/**
 * Shared createSubAccount/editSubAccount form behind `AddSubAccount` (parent known) and
 * `AddSubAccountWithParent` (parent chosen in the form).
 */
const SubAccountForm = ({ customer_id, subAccount, pickParent, invalidateKey }: SubAccountFormProps) => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const closePortal = usePortalContext();

	const subAccountLabel = getCustomizationValue(user, "sub_account_label", true) || "Sub Account";
	const customerLabel = getCustomizationValue(user, "customer_label", true) || "Customer";

	const initialValues: SubAccountFormValues = {
		uuid: subAccount?.uuid,
		firstname: subAccount?.firstname ?? "",
		lastname: subAccount?.lastname ?? "",
		othername: subAccount?.othername ?? "",
		idNo: subAccount?.idNo ?? "",
		customer_id: String(subAccount?.customer_id ?? customer_id ?? ""),
		businessUuid: user?.business_uuid ?? "",
	};

	const { mutate, isLoading } = useMutation(subAccount ? editSubAccount : createSubAccount, {
		onSuccess: ({ message }) => {
			showSuccessToast(message ?? "");
			queryClient.invalidateQueries([invalidateKey]);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-h-fit tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="First name"
							name="firstname"
							placeholder="First name"
							value={values.firstname}
							error={touched.firstname && errors.firstname}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Last name"
							name="lastname"
							placeholder="Last name"
							value={values.lastname}
							error={touched.lastname && errors.lastname}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Other name"
							name="othername"
							placeholder="Other name"
							value={values.othername}
							error={touched.othername && errors.othername}
							onChange={handleChange}
							hint="Optional"
						/>
						<TextField
							label="ID number"
							name="idNo"
							placeholder="ID number"
							value={values.idNo}
							error={touched.idNo && errors.idNo}
							onChange={handleChange}
							hint="Optional"
						/>
					</div>

					{pickParent && (
						<Field label={customerLabel} name="customer_id" required error={touched.customer_id && errors.customer_id}>
							<ComboSearch
								value={values.customer_id}
								onChange={(value: string) => setFieldValue("customer_id", value)}
								placeholder={`Search ${customerLabel}...`}
								className="tw-h-11 tw-w-full tw-justify-between"
								optionRenderer={({ first_name, last_name }: CustomerRow) => (
									<p>
										{first_name} {last_name}
									</p>
								)}
								queryKey="customers"
								queryFN={getCustomers}
								mapFN={(option: CustomerRow) => ({
									label: `${option.first_name} ${option.last_name}`,
									value: String(option.id),
									info: option,
								})}
							/>
						</Field>
					)}

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Saving..." : `${subAccount ? "Update" : "Add"} ${subAccountLabel}`}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default SubAccountForm;
